import type { ChangeEventHandler } from 'react'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import RadioInput from 'LuxKit/Radio/RadioInput'
import RadioButton from 'LuxKit/Radio/RadioButton'
import BodyText from 'LuxKit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import focusStyle from 'styles/utils/focusStyle'

const RadioAppearance = css`
  &:checked:not(:disabled) {
    --radio-color: ${props => props.theme.palette.highlight.primary.darkForeground};
  }
  &:not(:checked):not(:disabled) {
    --radio-color: ${props => props.theme.palette.neutral.surface.normalContrast};
  }
`

const ExclusiveSelection = styled(RadioInput)`
  align-items: flex-start;
  input {
    ${RadioAppearance}
    margin-block-start: ${rem(2)};
  }
`

const SelectionIndicator = styled(RadioButton)`
  ${RadioAppearance}
  margin-block-start: ${rem(2)};
`

const IndependentSelection = styled.label`
  position: relative;
  display: inline-flex;
  align-items: flex-start;
  gap: ${rem(12)};
`

const IndependentInput = styled.input`
  position: absolute;
  inset-inline-start: 0;
  inset-block-start: ${rem(2)};
  width: ${rem(20)};
  height: ${rem(20)};
  opacity: 0;

  &:focus-visible + input { ${focusStyle} }
`

interface Props {
  title: string
  name: string
  value: string
  selected: boolean
  multiple: boolean
  disabled: boolean
  /** Applied upgrade presentation only; the product controller owns the target and selection. */
  upgradeApplied?: boolean
  onChange: ChangeEventHandler<HTMLInputElement>
}

function PackageSelectionHeading({ title, name, value, selected, multiple, disabled, upgradeApplied = false, onChange }: Props) {
  const titleContent = <BodyText
    as="span"
    variant="large"
    weight={upgradeApplied ? 'normal' : 'bold'}
    colour={upgradeApplied ? 'neutral-four' : 'normal-contrast'}
    strikeThrough={upgradeApplied}
  >{title}
  </BodyText>
  return <>
    <CSSBreakpoint max="mobile">
      {multiple && <IndependentSelection>
        <IndependentInput type="checkbox" name={name} value={value} checked={selected} onChange={onChange} disabled={disabled} />
        {/* Reuse the site's radio appearance while keeping split bookings independently selectable. */}
        <SelectionIndicator checked={selected} disabled={disabled} />
        {titleContent}
      </IndependentSelection>}
      {!multiple && <ExclusiveSelection name={name} value={value} checked={selected} onChange={onChange} disabled={disabled}>
        {titleContent}
      </ExclusiveSelection>}
    </CSSBreakpoint>
    <CSSBreakpoint min="tablet">{titleContent}</CSSBreakpoint>
  </>
}

export default PackageSelectionHeading
